import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Wrench, Shield, Clock, CheckCircle, AlertTriangle, Search, Plus, RefreshCw,
  Phone, Mail, FileText, Send, Camera, Download, ChevronRight, ArrowLeft,
  Package, Calendar, User, MessageSquare, History, XCircle, Eye, Edit2,
  BarChart3, AlertCircle, Timer, Truck, Scale, Ban, Upload, PenLine, CheckSquare,
  Archive, ShoppingCart, Link2, Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs des statuts
const STATUS_COLORS = {
  en_attente: 'bg-gray-100 text-gray-800 border-gray-300',
  diagnostic: 'bg-blue-100 text-blue-800 border-blue-300',
  en_cours: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  attente_piece: 'bg-purple-100 text-purple-800 border-purple-300',
  tests_qualite: 'bg-orange-100 text-orange-800 border-orange-300',
  pret: 'bg-green-100 text-green-800 border-green-300',
  cloture: 'bg-slate-100 text-slate-800 border-slate-300',
  rejete: 'bg-red-100 text-red-800 border-red-300',
};

const STATUS_LABELS = {
  en_attente: 'En attente',
  diagnostic: 'Diagnostic',
  en_cours: 'En cours',
  attente_piece: 'Attente Pièce',
  tests_qualite: 'Tests Qualité',
  pret: 'Prêt',
  cloture: 'Clôturé',
  rejete: 'Rejeté',
};

const WARRANTY_TYPES = {
  appareil_origine: { label: 'Appareils (Origine)', months: 24, color: 'emerald', description: 'Garantie légale 2 ans' },
  piece_origine: { label: 'Pièces (Origine)', months: 12, color: 'blue', description: 'Service Pack' },
  compatible_premium: { label: 'Compatible Premium', months: 6, color: 'purple', description: 'Qualité supérieure' },
  entree_gamme: { label: 'Entrée de gamme', months: 3, color: 'orange', description: 'Minimum légal' },
  prestation_service: { label: 'Prestation de Service', months: 3, color: 'cyan', description: 'Main d\'œuvre' },
  vente: { label: 'Vente (Neuf/Reconditionné)', months: 24, color: 'green', description: 'Garantie légale 2 ans' },
};

const REPAIR_TYPES = {
  standard: { label: 'Standard', description: '20 min à 4h (si stock)' },
  commande: { label: 'Sur commande', description: '3 à 5 jours ouvrés' },
  expertise: { label: 'Expertise SAV', description: '7 à 10 jours ouvrés' },
  personnalise: { label: 'Personnalisé', description: 'Délai défini' },
};

// Décisions de conclusion - Dossiers Acceptés
const ACCEPTED_DECISIONS = {
  remboursement_total: {
    id: 'remboursement_total',
    label: 'Remboursement Total',
    icon: '💰',
    description: 'Le client bénéficie d\'un remboursement intégral du montant de l\'achat initial.',
    requiresAmount: true,
    color: 'green'
  },
  remboursement_partiel: {
    id: 'remboursement_partiel',
    label: 'Remboursement Partiel',
    icon: '💵',
    description: 'Le client bénéficie d\'un remboursement partiel.',
    requiresAmount: true,
    requiresPercentage: true,
    color: 'emerald'
  },
  avoir: {
    id: 'avoir',
    label: 'Avoir (Bon d\'achat)',
    icon: '🎫',
    description: 'Le client reçoit un avoir valable sur un prochain achat (12 mois).',
    requiresAmount: true,
    color: 'blue'
  },
  remplacement_identique: {
    id: 'remplacement_identique',
    label: 'Remplacement Identique',
    icon: '🔄',
    description: 'Le produit est remplacé par un produit identique. Nouvelle garantie.',
    newWarranty: true,
    color: 'purple'
  },
  remplacement_equivalent: {
    id: 'remplacement_equivalent',
    label: 'Remplacement Équivalent',
    icon: '↔️',
    description: 'Le produit est remplacé par un équivalent sans soulte.',
    newWarranty: true,
    color: 'indigo'
  },
  remplacement_avec_ecart: {
    id: 'remplacement_avec_ecart',
    label: 'Remplacement avec Écart',
    icon: '📊',
    description: 'Remplacement par un produit supérieur. Le client règle la différence.',
    requiresAmount: true,
    requiresSupplement: true,
    newWarranty: true,
    color: 'orange'
  },
  reparation_garantie: {
    id: 'reparation_garantie',
    label: 'Réparation sous Garantie',
    icon: '🔧',
    description: 'Réparation gratuite dans le cadre de la garantie.',
    freeRepair: true,
    color: 'teal'
  },
};

// Décisions de conclusion - Dossiers Rejetés
const REJECTED_DECISIONS = {
  aucun_remboursement: {
    id: 'aucun_remboursement',
    label: 'Aucun Remboursement',
    icon: '❌',
    description: 'La demande de remboursement est refusée.',
    requiresReason: true,
    requiresPhoto: true,
    color: 'red'
  },
  aucun_remplacement: {
    id: 'aucun_remplacement',
    label: 'Aucun Remplacement',
    icon: '🚫',
    description: 'La demande de remplacement est refusée.',
    requiresReason: true,
    requiresPhoto: true,
    color: 'red'
  },
  geste_commercial_remise: {
    id: 'geste_commercial_remise',
    label: 'Geste Commercial - Remise',
    icon: '🎁',
    description: 'Un geste commercial est accordé (remise sur réparation/achat).',
    requiresAmount: true,
    requiresPercentage: true,
    isCommercialGesture: true,
    color: 'amber'
  },
  geste_commercial_prix_reduit: {
    id: 'geste_commercial_prix_reduit',
    label: 'Geste Commercial - Prix Réduit',
    icon: '🏷️',
    description: 'Produit de remplacement proposé à prix réduit.',
    requiresAmount: true,
    isCommercialGesture: true,
    color: 'yellow'
  },
  reparation_payante: {
    id: 'reparation_payante',
    label: 'Réparation Payante',
    icon: '💳',
    description: 'La garantie ne s\'applique pas. Réparation payante proposée avec devis.',
    requiresAmount: true,
    requiresQuote: true,
    color: 'slate'
  },
};

// Composant Canvas pour signature
const SignatureCanvas = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear?.();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave?.(dataUrl);
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="border-2 border-dashed border-gray-300 rounded-lg bg-white cursor-crosshair touch-none"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>
          Effacer
        </Button>
        <Button type="button" size="sm" onClick={saveSignature} className="bg-blue-600">
          <PenLine className="h-4 w-4 mr-1" />
          Valider signature
        </Button>
      </div>
    </div>
  );
};

export default function SAVPage({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dossiers, setDossiers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Recherche de clients existants
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Historique ventes (bons d'entrées et commandes archivées)
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesSearch, setSalesSearch] = useState('');
  const [loadingSales, setLoadingSales] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  
  // Historique signatures
  const [signaturesHistory, setSignaturesHistory] = useState([]);
  const [signaturesSearch, setSignaturesSearch] = useState('');
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  
  // Polling signature en temps réel
  const [pollingDossierId, setPollingDossierId] = useState(null);
  
  // Dossiers supprimés
  const [deletedDossiers, setDeletedDossiers] = useState([]);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showConclusionModal, setShowConclusionModal] = useState(false);
  const [signatureType, setSignatureType] = useState('entry'); // entry ou exit
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [printData, setPrintData] = useState(null);
  
  // Formulaire de conclusion
  const initialConclusionState = {
    decision_type: '',
    custom_description: '',
    amount: null,
    percentage: null,
    supplement_amount: null,
    new_product_description: '',
    quote_reference: '',
    technician_notes: '',
    rejection_reason: '',
    rejection_photos: [],
  };
  const [conclusionForm, setConclusionForm] = useState(initialConclusionState);
  
  // État initial du formulaire nouveau dossier
  const initialDossierState = {
    client_name: '',
    client_phone: '',
    client_email: '',
    client_id: null,
    product_type: 'entree_gamme',
    product_description: '',
    serial_number: '',
    imei: '',
    purchase_date: '',
    purchase_source: 'manual',
    purchase_amount: null,
    problem_description: '',
    entry_condition: '',
    notes: '',
    entry_signature: null,
    repair_type: 'standard',
    custom_delay_days: null,
    custom_delay_weeks: null,
    data_backup_confirmed: false,
    checklist_completed: {
      sticker_intact: false,
      no_choc: false,
      no_liquide: false,
      garantie_active: false,
      etat_esthetique: false,
      data_backup: false,
    }
  };
  
  // Formulaire nouveau dossier avec checklist
  const [newDossier, setNewDossier] = useState(initialDossierState);
  
  // Formulaire de rejet (photo obligatoire)
  const [rejectForm, setRejectForm] = useState({
    rejection_reason: '',
    rejection_notes: '',
    rejection_photos: [],
  });
  
  // Message
  const [messageForm, setMessageForm] = useState({
    message_type: 'sms',
    message: '',
  });

  const token = localStorage.getItem('token');

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Charger la config
  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sav/config`, { headers: getAuthHeaders() });
      if (res.ok) setConfig(await res.json());
    } catch (e) {
      console.error('Erreur config SAV:', e);
    }
  }, [getAuthHeaders]);

  // Charger le dashboard
  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sav/dashboard`, { headers: getAuthHeaders() });
      if (res.ok) setDashboard(await res.json());
    } catch (e) {
      console.error('Erreur dashboard SAV:', e);
    }
  }, [getAuthHeaders]);

  // Charger les dossiers
  const loadDossiers = useCallback(async () => {
    try {
      let url = `${API}/api/sav/dossiers?limit=200`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDossiers(data.dossiers || []);
      }
    } catch (e) {
      console.error('Erreur chargement dossiers:', e);
    }
  }, [getAuthHeaders, statusFilter, search]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadConfig(), loadDashboard(), loadDossiers()]);
      setLoading(false);
    };
    init();
  }, [loadConfig, loadDashboard, loadDossiers]);

  useEffect(() => {
    loadDossiers();
  }, [statusFilter, search, loadDossiers]);

  // Charger l'historique des ventes (bons d'entrée et commandes archivées)
  const loadSalesHistory = useCallback(async () => {
    setLoadingSales(true);
    try {
      // Charger les bons d'entrée archivés (storage)
      const storageRes = await fetch(`${API}/api/storage?status=archived&limit=100`, { headers: getAuthHeaders() });
      let storageItems = [];
      if (storageRes.ok) {
        const storageData = await storageRes.json();
        storageItems = (storageData.items || []).map(item => ({
          ...item,
          source: 'storage',
          source_label: 'Bon d\'entrée',
          sale_date: item.entry_date || item.created_at,
          description: item.device_description || `${item.brand} ${item.model}`,
          total: item.repair_cost || item.total || 0
        }));
      }
      
      // Charger les commandes archivées (orders)
      const ordersRes = await fetch(`${API}/api/orders?status=completed&limit=100`, { headers: getAuthHeaders() });
      let orderItems = [];
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        orderItems = (ordersData.orders || ordersData || []).map(order => ({
          ...order,
          source: 'order',
          source_label: 'Commande',
          sale_date: order.order_date || order.created_at,
          description: order.items?.map(i => i.name || i.description).join(', ') || order.description,
          total: order.total || 0
        }));
      }
      
      // Combiner et trier par date
      const combined = [...storageItems, ...orderItems].sort((a, b) => 
        new Date(b.sale_date) - new Date(a.sale_date)
      );
      
      setSalesHistory(combined);
    } catch (e) {
      console.error('Erreur chargement historique ventes:', e);
    } finally {
      setLoadingSales(false);
    }
  }, [getAuthHeaders]);

  // Envoyer une notification manuelle
  const handleManualNotification = async (notificationType) => {
    if (!selectedDossier) return;
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${selectedDossier.id}/notify`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notification_type: notificationType })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`Notification "${notificationType}" envoyée au client`);
        setShowNotifyModal(false);
      } else {
        toast.error(data.error || 'Erreur envoi notification');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };

  // Associer une vente à un dossier SAV
  const handleLinkSaleToDossier = async (sale) => {
    setSelectedSale(sale);
    
    // Pré-remplir le formulaire de création avec les données de la vente
    setNewDossier({
      ...initialDossierState,
      client_name: sale.client_name || sale.customer_name || '',
      client_phone: sale.client_phone || sale.phone || '',
      client_email: sale.client_email || sale.email || '',
      product_description: sale.description || '',
      purchase_date: sale.sale_date?.split('T')[0] || '',
      purchase_amount: sale.total || 0,
      purchase_invoice_id: sale.id,
      notes: `Associé à ${sale.source_label} #${sale.id}`,
    });
    
    setShowLinkModal(false);
    setShowCreateModal(true);
    toast.info(`Données importées depuis ${sale.source_label} #${sale.id}`);
  };

  // Charger les ventes quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'history') {
      loadSalesHistory();
    }
    if (activeTab === 'signatures') {
      loadSignaturesHistory();
    }
  }, [activeTab, loadSalesHistory]);

  // Charger l'historique des signatures
  const loadSignaturesHistory = async () => {
    setLoadingSignatures(true);
    try {
      const params = new URLSearchParams();
      if (signaturesSearch) params.append('search', signaturesSearch);
      params.append('limit', '100');
      
      const res = await fetch(`${API}/api/sav/signatures/history?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setSignaturesHistory(data.history || []);
      }
    } catch (e) {
      console.error('Erreur chargement historique signatures:', e);
    }
    setLoadingSignatures(false);
  };

  // Polling pour détecter les signatures en temps réel
  useEffect(() => {
    let interval;
    if (pollingDossierId) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`${API}/api/sav/dossiers/${pollingDossierId}/signature-status`, {
            headers: getAuthHeaders()
          });
          const data = await res.json();
          
          if (data.entry_signed && !selectedDossier?.entry_signature) {
            toast.success('✅ Signature d\'entrée confirmée !');
            loadDossiers();
            if (selectedDossier?.id === pollingDossierId) {
              setSelectedDossier(prev => ({
                ...prev,
                entry_signature: true,
                entry_signature_date: data.entry_signature_date,
                entry_signature_method: data.entry_signature_method,
                entry_signature_remote: data.entry_signature_remote
              }));
            }
          }
          
          if (data.exit_signed && !selectedDossier?.exit_signature) {
            toast.success('✅ Signature de sortie confirmée !');
            loadDossiers();
            setPollingDossierId(null);
          }
        } catch (e) {
          console.error('Erreur polling signature:', e);
        }
      }, 5000); // Vérifier toutes les 5 secondes
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pollingDossierId, selectedDossier]);

  // Rafraîchir un dossier spécifique
  const refreshDossier = async (dossierId) => {
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${dossierId}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data && data.id) {
        setSelectedDossier(data);
        // Mettre à jour dans la liste
        setDossiers(prev => prev.map(d => d.id === dossierId ? data : d));
        toast.success('Dossier actualisé');
      }
    } catch (e) {
      toast.error('Erreur actualisation');
    }
  };

  // Créer un dossier
  const handleCreateDossier = async () => {
    if (!newDossier.client_name || !newDossier.client_phone || !newDossier.product_description || !newDossier.problem_description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return null;
    }
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newDossier)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Dossier ${data.dossier.id} créé avec succès`);
        setShowCreateModal(false);
        setNewDossier(initialDossierState); // Reset complet avec checklist
        loadDossiers();
        loadDashboard();
        
        // Notification automatique à la création (le backend l'envoie déjà)
        toast.info('Notification envoyée au client');
        
        return { dossier_id: data.dossier.id, phone: newDossier.client_phone };
      } else {
        toast.error(data.message || data.error || 'Erreur création');
        return null;
      }
    } catch (e) {
      console.error('Erreur création dossier:', e);
      toast.error('Erreur de connexion');
      return null;
    }
  };

  // Changer le statut
  const handleStatusChange = async (dossierId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${dossierId}/status?status=${newStatus}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success(`Statut mis à jour: ${STATUS_LABELS[newStatus]}`);
        loadDossiers();
        loadDashboard();
        if (selectedDossier?.id === dossierId) {
          setSelectedDossier({ ...selectedDossier, status: newStatus });
        }
      }
    } catch (e) {
      toast.error('Erreur changement statut');
    }
  };

  // Rechercher des clients existants
  const searchClients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setClientResults([]);
      setShowClientDropdown(false);
      return;
    }
    
    setLoadingClients(true);
    try {
      const res = await fetch(`${API}/api/sav/search-clients?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setClientResults(data.clients || []);
        setShowClientDropdown(true);
      }
    } catch (e) {
      console.error('Erreur recherche clients:', e);
    } finally {
      setLoadingClients(false);
    }
  }, [getAuthHeaders]);

  // Sélectionner un client de la recherche
  const handleSelectClient = (client) => {
    setNewDossier(prev => ({
      ...prev,
      client_name: client.name,
      client_phone: client.phone,
      client_email: client.email,
      client_id: client.id,
    }));
    setSelectedClient(client);
    setShowClientDropdown(false);
    setClientSearch(client.name);
    
    // Si le client a des achats, proposer d'importer la date
    if (client.purchases?.length > 0) {
      toast.info(`${client.purchases.length} achat(s) trouvé(s). Sélectionnez pour importer la date.`);
    }
  };

  // Envoyer un lien de signature à distance
  const handleSendRemoteSignatureLink = async (dossierId, signatureType) => {
    const sigLabel = signatureType === 'entry' ? "d'entrée" : "de sortie";
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${dossierId}/remote-signature/generate?signature_type=${signatureType}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`Lien de signature ${sigLabel} envoyé !`, {
          description: `Via ${data.sent_via.join(', ')}. Expire dans 7 jours.`
        });
        loadDossiers(); // Rafraîchir pour voir le statut
      } else {
        toast.error(data.detail || `Erreur envoi lien ${sigLabel}`);
      }
    } catch (e) {
      console.error('Erreur envoi lien signature:', e);
      toast.error('Erreur de connexion');
    }
  };

  // Importer une date d'achat depuis l'historique
  const handleImportPurchaseDate = (purchase) => {
    if (purchase.date) {
      setNewDossier(prev => ({
        ...prev,
        purchase_date: purchase.date.split('T')[0],
        purchase_source: purchase.source,
        purchase_amount: purchase.total,
        product_description: prev.product_description || purchase.description,
      }));
      toast.success(`Date d'achat importée: ${purchase.date.split('T')[0]}`);
    }
  };

  // Supprimer un dossier
  const handleDeleteDossier = async (dossierId, permanent = false) => {
    if (!confirm(permanent ? 'Supprimer définitivement ce dossier ?' : 'Archiver ce dossier ?')) return;
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${dossierId}?permanent=${permanent}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success(permanent ? 'Dossier supprimé définitivement' : 'Dossier archivé');
        loadDossiers();
        loadDashboard();
        setShowDetailModal(false);
      }
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  // Restaurer un dossier supprimé
  const handleRestoreDossier = async (dossierId) => {
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${dossierId}/restore`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Dossier restauré');
        loadDeletedDossiers();
        loadDossiers();
        loadDashboard();
      }
    } catch (e) {
      toast.error('Erreur restauration');
    }
  };

  // Charger les dossiers supprimés
  const loadDeletedDossiers = async () => {
    try {
      const res = await fetch(`${API}/api/sav/deleted`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDeletedDossiers(data.dossiers || []);
      }
    } catch (e) {
      console.error('Erreur chargement dossiers supprimés:', e);
    }
  };

  // Réouvrir un dossier clôturé
  const handleReopenDossier = async (reason, newStatus = 'en_attente') => {
    if (!selectedDossier) return;
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${selectedDossier.id}/reopen`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason, new_status: newStatus })
      });
      
      if (res.ok) {
        toast.success('Dossier réouvert');
        setShowReopenModal(false);
        loadDossiers();
        loadDashboard();
        setSelectedDossier(prev => ({ ...prev, status: newStatus }));
      }
    } catch (e) {
      toast.error('Erreur réouverture');
    }
  };

  // Charger les données d'impression
  const handleLoadPrintData = async (dossierId) => {
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${dossierId}/print-data`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPrintData(data);
        setShowPrintModal(true);
      }
    } catch (e) {
      toast.error('Erreur chargement données impression');
    }
  };

  // Envoyer la fiche SAV par SMS/Email
  const handleSendSheet = async (sendSms, sendEmail) => {
    if (!selectedDossier) return;
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${selectedDossier.id}/send-sheet`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ send_sms: sendSms, send_email: sendEmail })
      });
      
      const data = await res.json();
      if (data.success) {
        let msg = 'Fiche envoyée';
        if (data.sms_sent) msg += ' par SMS';
        if (data.email_sent) msg += (data.sms_sent ? ' et' : ' par') + ' Email';
        toast.success(msg);
      }
    } catch (e) {
      toast.error('Erreur envoi fiche');
    }
  };

  // Debounce pour la recherche client
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearch.length >= 2) {
        searchClients(clientSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch, searchClients]);

  // Envoyer un message
  const handleSendMessage = async () => {
    if (!messageForm.message || !selectedDossier) return;
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${selectedDossier.id}/message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          dossier_id: selectedDossier.id,
          message_type: messageForm.message_type,
          message: messageForm.message,
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Message envoyé');
        setShowMessageModal(false);
        setMessageForm({ message_type: 'sms', message: '' });
      }
    } catch (e) {
      toast.error('Erreur envoi message');
    }
  };

  // Rejeter un dossier (avec photo obligatoire)
  const handleRejectDossier = async () => {
    if (!selectedDossier || !rejectForm.rejection_reason) {
      toast.error('Sélectionnez un motif de rejet');
      return;
    }
    
    const rejectionInfo = config?.rejection_reasons?.[rejectForm.rejection_reason];
    if (rejectionInfo?.requires_photo && rejectForm.rejection_photos.length === 0) {
      toast.error('Une photo de preuve est OBLIGATOIRE pour ce motif de rejet');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${selectedDossier.id}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(rejectForm)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Dossier rejeté. Le client a été notifié.');
        setShowRejectModal(false);
        setRejectForm({ rejection_reason: '', rejection_notes: '', rejection_photos: [] });
        loadDossiers();
        loadDashboard();
        setShowDetailModal(false);
      } else {
        toast.error(data.detail || 'Erreur rejet');
      }
    } catch (e) {
      toast.error('Erreur rejet dossier');
    }
  };

  // Enregistrer une signature
  const handleSaveSignature = async (signatureData) => {
    if (!selectedDossier) return;
    
    try {
      const endpoint = signatureType === 'entry' ? 'signature/entry' : 'signature/exit';
      const res = await fetch(`${API}/api/sav/dossiers/${selectedDossier.id}/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ signature: signatureData })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(signatureType === 'entry' ? 'Signature d\'entrée enregistrée' : 'Signature de sortie enregistrée. Dossier clôturé.');
        setShowSignatureModal(false);
        loadDossiers();
        loadDashboard();
        
        // Mettre à jour le dossier sélectionné
        if (signatureType === 'exit') {
          setSelectedDossier(prev => ({ ...prev, status: 'cloture' }));
        }
      } else {
        toast.error(data.detail || 'Erreur signature');
      }
    } catch (e) {
      toast.error('Erreur enregistrement signature');
    }
  };

  // Conclure un dossier (nouvelle fonctionnalité)
  const handleConcludeDossier = async () => {
    if (!selectedDossier || !conclusionForm.decision_type) {
      toast.error('Veuillez sélectionner une décision');
      return;
    }
    
    const allDecisions = { ...ACCEPTED_DECISIONS, ...REJECTED_DECISIONS };
    const decisionInfo = allDecisions[conclusionForm.decision_type];
    
    // Validation stricte
    if (decisionInfo?.requiresAmount && !conclusionForm.amount) {
      toast.error('Le montant est obligatoire pour cette décision');
      return;
    }
    
    // Si c'est un rejet (pas un geste commercial), vérifier motif + photo
    const isRejection = REJECTED_DECISIONS[conclusionForm.decision_type];
    if (isRejection && !decisionInfo?.isCommercialGesture) {
      if (!conclusionForm.rejection_reason) {
        toast.error('Un motif de rejet est OBLIGATOIRE');
        return;
      }
      
      const rejectionInfo = config?.rejection_reasons?.[conclusionForm.rejection_reason];
      if (rejectionInfo?.requires_photo && conclusionForm.rejection_photos.length === 0) {
        toast.error('Une photo de preuve est OBLIGATOIRE pour ce motif de rejet');
        return;
      }
    }
    
    try {
      const res = await fetch(`${API}/api/sav/dossiers/${selectedDossier.id}/conclude`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(conclusionForm)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Dossier conclu: ${decisionInfo?.label || 'Décision enregistrée'}`);
        setShowConclusionModal(false);
        setConclusionForm(initialConclusionState);
        loadDossiers();
        loadDashboard();
        setShowDetailModal(false);
      } else {
        toast.error(data.detail || data.message || 'Erreur conclusion');
      }
    } catch (e) {
      console.error('Erreur conclusion:', e);
      toast.error('Erreur de connexion');
    }
  };

  // Upload photo pour conclusion de rejet
  const handleConclusionPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setConclusionForm(prev => ({
        ...prev,
        rejection_photos: [...prev.rejection_photos, reader.result]
      }));
    };
    reader.readAsDataURL(file);
  };

  // Upload photo de preuve pour rejet
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setRejectForm(prev => ({
        ...prev,
        rejection_photos: [...prev.rejection_photos, reader.result]
      }));
    };
    reader.readAsDataURL(file);
  };

  // Export
  const handleExport = async (format) => {
    try {
      const res = await fetch(`${API}/api/sav/export?format=${format}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      
      if (format === 'csv' && data.content) {
        const blob = new Blob([data.content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'sav_export.csv';
        a.click();
        toast.success('Export téléchargé');
      }
    } catch (e) {
      toast.error('Erreur export');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/')} 
              variant="outline" 
              size="sm"
              className="gap-2"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour Caisse
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Wrench className="h-8 w-8 text-blue-600" />
                SAV & Réparations
              </h1>
              <p className="text-slate-600 mt-1">Gestion des retours et garanties</p>
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => { loadDeletedDossiers(); setShowDeletedModal(true); }} variant="outline" className="gap-2">
              <Archive className="h-4 w-4" />
              Supprimés
            </Button>
            <Button onClick={() => handleExport('csv')} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4" />
              Nouveau Dossier
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="sav-tab-dashboard">
              <BarChart3 className="h-4 w-4" />
              Tableau de bord
            </TabsTrigger>
            <TabsTrigger value="dossiers" className="gap-2" data-testid="sav-tab-dossiers">
              <FileText className="h-4 w-4" />
              Dossiers
              {dashboard?.total_dossiers > 0 && (
                <Badge variant="secondary">{dashboard.total_dossiers}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="overdue" className="gap-2" data-testid="sav-tab-overdue">
              <AlertTriangle className="h-4 w-4" />
              En retard
              {dashboard?.overdue_count > 0 && (
                <Badge variant="destructive">{dashboard.overdue_count}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="ready" className="gap-2" data-testid="sav-tab-ready">
              <CheckCircle className="h-4 w-4" />
              Prêts
              {dashboard?.ready_count > 0 && (
                <Badge className="bg-green-500">{dashboard.ready_count}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2" data-testid="sav-tab-history">
              <Archive className="h-4 w-4" />
              Historique Ventes
            </TabsTrigger>
            <TabsTrigger value="signatures" className="gap-2" data-testid="sav-tab-signatures">
              <PenLine className="h-4 w-4" />
              Historique Signatures
            </TabsTrigger>
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              {Object.entries(STATUS_LABELS).filter(([k]) => k !== 'rejete').map(([key, label]) => (
                <Card key={key} className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === key ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => { setStatusFilter(key); setActiveTab('dossiers'); }}>
                  <CardContent className="p-4 text-center">
                    <div className={`text-3xl font-bold ${key === 'pret' ? 'text-green-600' : key === 'en_attente' ? 'text-gray-600' : 'text-blue-600'}`}>
                      {dashboard?.stats_by_status?.[key] || 0}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">{label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Alertes */}
            {dashboard?.overdue_count > 0 && (
              <Card className="border-red-200 bg-red-50 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    {dashboard.overdue_count} dossier(s) en retard
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dashboard.overdue_dossiers?.slice(0, 3).map(d => (
                      <div key={d.id} className="flex items-center justify-between p-2 bg-white rounded border border-red-100">
                        <div>
                          <span className="font-medium">{d.id}</span>
                          <span className="text-slate-600 ml-2">{d.client_name}</span>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedDossier(d); setShowDetailModal(true); }}>
                          Voir
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Garanties */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-600" />
                  Types de garanties
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(WARRANTY_TYPES).map(([key, info]) => (
                    <div key={key} className={`p-4 rounded-lg border-2 border-${info.color}-200 bg-${info.color}-50`}>
                      <div className={`text-2xl font-bold text-${info.color}-600`}>{info.months} mois</div>
                      <div className="text-sm text-slate-700 font-medium">{info.label}</div>
                      <div className="text-xs text-slate-500 mt-1">{info.description}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Délais opérationnels */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-blue-600" />
                  Délais opérationnels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {config?.operational_delays && Object.entries(config.operational_delays).map(([key, info]) => (
                    <div key={key} className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                      <div className="font-medium text-blue-800">{info.label}</div>
                      <div className="text-sm text-blue-600 mt-1">{info.description}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cadre juridique */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-slate-600" />
                  Cadre juridique (conformité France)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {config?.legal_framework && Object.entries(config.legal_framework).map(([key, info]) => (
                    <div key={key} className="p-3 rounded-lg border bg-slate-50">
                      <div className="font-mono text-xs text-slate-500">{info.articles}</div>
                      <div className="text-sm text-slate-700 mt-1">{info.description}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="text-xs text-amber-800 font-medium mb-1">Mention légale (sur chaque devis/fiche)</div>
                  <div className="text-xs text-amber-700">{config?.legal_mention}</div>
                </div>

                {/* Clause d'exclusivité technique */}
                {config?.exclusivity_clause && (
                  <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                    <div className="text-sm text-red-800 font-bold mb-2 flex items-center gap-2">
                      <Ban className="h-4 w-4" />
                      {config.exclusivity_clause.title}
                    </div>
                    <div className="text-xs text-red-700 font-medium mb-2">
                      ⚠️ {config.exclusivity_clause.rule}
                    </div>
                    <ul className="text-xs text-red-600 space-y-1 list-disc list-inside">
                      {config.exclusivity_clause.conditions?.map((condition, i) => (
                        <li key={i}>{condition}</li>
                      ))}
                    </ul>
                    <div className="mt-2 pt-2 border-t border-red-200">
                      <div className="text-xs text-red-700 font-medium">Base juridique:</div>
                      {config.exclusivity_clause.legal_basis?.map((basis, i) => (
                        <div key={i} className="text-xs text-red-600 mt-1">• {basis}</div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Liste des dossiers */}
          <TabsContent value="dossiers">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle>Tous les dossiers</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        {Object.entries(STATUS_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dossiers.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Wrench className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Aucun dossier trouvé</p>
                    </div>
                  ) : (
                    dossiers.map(dossier => (
                      <div key={dossier.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="font-semibold text-slate-900">{dossier.id}</div>
                            <div className="text-sm text-slate-600">{dossier.client_name}</div>
                          </div>
                          <div className="hidden md:block">
                            <div className="text-sm text-slate-700">{dossier.product_description}</div>
                            <div className="text-xs text-slate-500">
                              {WARRANTY_TYPES[dossier.product_type]?.label || dossier.product_type}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={STATUS_COLORS[dossier.status]}>
                            {STATUS_LABELS[dossier.status]}
                          </Badge>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => { setSelectedDossier(dossier); setShowDetailModal(true); }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setSelectedDossier(dossier); setShowMessageModal(true); }}>
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* En retard */}
          <TabsContent value="overdue">
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <Timer className="h-5 w-5" />
                  Dossiers en retard (+48h)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.overdue_dossiers?.length === 0 ? (
                  <div className="text-center py-8 text-green-600">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2" />
                    <p>Aucun dossier en retard !</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard?.overdue_dossiers?.map(d => (
                      <div key={d.id} className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                        <div>
                          <div className="font-semibold">{d.id} - {d.client_name}</div>
                          <div className="text-sm text-slate-600">{d.product_description}</div>
                          <div className="text-xs text-red-600 mt-1">
                            Créé le {new Date(d.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Select onValueChange={(v) => handleStatusChange(d.id, v)}>
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Action" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="diagnostic">Passer en Diagnostic</SelectItem>
                              <SelectItem value="en_cours">Passer en Cours</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="outline" onClick={() => { setSelectedDossier(d); setShowDetailModal(true); }}>
                            Détails
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prêts */}
          <TabsContent value="ready">
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-green-700 flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Prêts à remettre
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.ready_dossiers?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun appareil prêt pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard?.ready_dossiers?.map(d => (
                      <div key={d.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                        <div>
                          <div className="font-semibold">{d.id} - {d.client_name}</div>
                          <div className="text-sm text-slate-600">{d.product_description}</div>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {d.client_phone}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setSelectedDossier(d); setShowMessageModal(true); }}>
                            <Send className="h-4 w-4 mr-1" /> Rappel
                          </Button>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleStatusChange(d.id, 'cloture')}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Clôturer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Historique Ventes */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5" />
                  Historique des Ventes & Réparations
                </CardTitle>
                <CardDescription>
                  Importez des données depuis les bons d'entrée ou commandes archivées pour créer un dossier SAV
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Recherche */}
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher par nom, téléphone, description..."
                      value={salesSearch}
                      onChange={(e) => setSalesSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={loadSalesHistory} variant="outline" className="gap-2">
                    <RefreshCw className={`h-4 w-4 ${loadingSales ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>

                {/* Liste des ventes */}
                {loadingSales ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                    <p className="text-slate-500 mt-2">Chargement...</p>
                  </div>
                ) : salesHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Aucune vente ou réparation archivée trouvée</p>
                    <p className="text-xs mt-1">Les bons d'entrée et commandes archivées apparaîtront ici</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {salesHistory
                      .filter(sale => {
                        if (!salesSearch) return true;
                        const search = salesSearch.toLowerCase();
                        return (
                          sale.client_name?.toLowerCase().includes(search) ||
                          sale.customer_name?.toLowerCase().includes(search) ||
                          sale.client_phone?.includes(search) ||
                          sale.phone?.includes(search) ||
                          sale.description?.toLowerCase().includes(search) ||
                          sale.id?.toLowerCase().includes(search)
                        );
                      })
                      .map(sale => (
                        <div 
                          key={`${sale.source}-${sale.id}`} 
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border hover:border-blue-300 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={sale.source === 'storage' ? 'secondary' : 'outline'}>
                                {sale.source === 'storage' ? <Package className="h-3 w-3 mr-1" /> : <ShoppingCart className="h-3 w-3 mr-1" />}
                                {sale.source_label}
                              </Badge>
                              <span className="font-mono text-xs text-slate-500">#{sale.id}</span>
                            </div>
                            <div className="font-semibold">{sale.client_name || sale.customer_name || 'Client inconnu'}</div>
                            <div className="text-sm text-slate-600 truncate max-w-md">{sale.description}</div>
                            <div className="flex gap-4 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('fr-FR') : 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {sale.client_phone || sale.phone || 'N/A'}
                              </span>
                              {sale.total > 0 && (
                                <span className="font-medium text-green-600">
                                  {sale.total.toFixed(2)} €
                                </span>
                              )}
                            </div>
                          </div>
                          <Button 
                            onClick={() => handleLinkSaleToDossier(sale)}
                            className="gap-2 bg-blue-600 hover:bg-blue-700"
                            data-testid="link-sale-btn"
                          >
                            <Link2 className="h-4 w-4" />
                            Créer SAV
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== ONGLET HISTORIQUE SIGNATURES ========== */}
          <TabsContent value="signatures">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PenLine className="h-5 w-5" />
                  Historique des Signatures (Système de Preuves)
                </CardTitle>
                <CardDescription>
                  Traçabilité complète : date, heure, méthode, notifications envoyées
                </CardDescription>
                
                {/* Recherche */}
                <div className="flex gap-2 mt-4">
                  <Input
                    placeholder="Rechercher par nom, n° dossier ou téléphone..."
                    value={signaturesSearch}
                    onChange={(e) => setSignaturesSearch(e.target.value)}
                    className="flex-1"
                    data-testid="signatures-search-input"
                  />
                  <Button onClick={loadSignaturesHistory} variant="outline">
                    <Search className="h-4 w-4 mr-1" />
                    Rechercher
                  </Button>
                  <Button onClick={loadSignaturesHistory} variant="outline">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingSignatures ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                    <p className="text-slate-500 mt-2">Chargement...</p>
                  </div>
                ) : signaturesHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <PenLine className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>Aucun historique de signature</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {signaturesHistory.map((item) => (
                      <div key={item.dossier_id} className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="font-semibold text-lg">{item.dossier_id}</div>
                            <div className="text-sm text-slate-600">{item.client_name} • {item.client_phone}</div>
                            {item.client_email && <div className="text-xs text-slate-400">{item.client_email}</div>}
                          </div>
                          <Badge className={STATUS_COLORS[item.status] || 'bg-slate-100'}>
                            {STATUS_LABELS[item.status] || item.status}
                          </Badge>
                        </div>
                        
                        <div className="text-sm text-slate-600 mb-3">{item.product}</div>
                        
                        {/* Signatures */}
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          {/* Signature Entrée */}
                          <div className={`p-3 rounded-lg ${item.entry_signature?.signed ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                            <div className="font-semibold text-sm mb-1">
                              {item.entry_signature?.signed ? '✅ Signature Entrée' : '⏳ En attente (Entrée)'}
                            </div>
                            {item.entry_signature?.signed && (
                              <>
                                <div className="text-xs">📅 {new Date(item.entry_signature.date).toLocaleString('fr-FR')}</div>
                                <div className="text-xs">📱 {item.entry_signature.remote ? 'À distance (SMS/OTP)' : 'Sur place'}</div>
                                {item.entry_signature.ip && <div className="text-xs text-slate-400">IP: {item.entry_signature.ip}</div>}
                              </>
                            )}
                          </div>
                          
                          {/* Signature Sortie */}
                          <div className={`p-3 rounded-lg ${item.exit_signature?.signed ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'}`}>
                            <div className="font-semibold text-sm mb-1">
                              {item.exit_signature?.signed ? '✅ Signature Sortie' : '⏳ En attente (Sortie)'}
                            </div>
                            {item.exit_signature?.signed && (
                              <>
                                <div className="text-xs">📅 {new Date(item.exit_signature.date).toLocaleString('fr-FR')}</div>
                                <div className="text-xs">📱 {item.exit_signature.remote ? 'À distance (SMS/OTP)' : 'Sur place'}</div>
                                {item.exit_signature.ip && <div className="text-xs text-slate-400">IP: {item.exit_signature.ip}</div>}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Lien signature distant actif */}
                        {item.remote_link?.active && !item.entry_signature?.signed && (
                          <div className="p-2 bg-purple-50 border border-purple-200 rounded text-xs mb-3">
                            <span className="font-semibold text-purple-700">🔗 Lien signature actif</span>
                            <span className="ml-2 text-purple-600">Expire: {new Date(item.remote_link.expires_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                        )}
                        
                        {/* Historique des notifications */}
                        {item.notifications && item.notifications.length > 0 && (
                          <div className="border-t pt-3 mt-3">
                            <div className="text-xs font-semibold text-slate-600 mb-2">📧 Notifications envoyées :</div>
                            <div className="space-y-1">
                              {item.notifications.slice(-5).map((notif, idx) => (
                                <div key={idx} className="text-xs flex items-center gap-2 text-slate-500">
                                  <span className="w-24 text-slate-400">
                                    {notif.timestamp ? new Date(notif.timestamp).toLocaleString('fr-FR', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}) : 'N/A'}
                                  </span>
                                  <Badge variant="outline" className="text-xs">{notif.type}</Badge>
                                  {notif.sms_sent && <span className="text-green-600">📱 SMS</span>}
                                  {notif.email_sent && <span className="text-blue-600">📧 Email</span>}
                                  {notif.signature_link && <span className="text-purple-600">🔗 Lien</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Bouton voir dossier */}
                        <div className="mt-3 flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const dossier = dossiers.find(d => d.id === item.dossier_id);
                              if (dossier) {
                                setSelectedDossier(dossier);
                                setShowDetailModal(true);
                              } else {
                                // Charger le dossier
                                fetch(`${API}/api/sav/dossiers/${item.dossier_id}`, { headers: getAuthHeaders() })
                                  .then(r => r.json())
                                  .then(data => {
                                    if (data.id) {
                                      setSelectedDossier(data);
                                      setShowDetailModal(true);
                                    }
                                  });
                              }
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Voir le dossier
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Création */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          // Réinitialiser le formulaire quand on ferme le modal
          setNewDossier(initialDossierState);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nouveau Dossier SAV
            </DialogTitle>
            <DialogDescription>
              Créez un dossier pour une demande de retour ou réparation sous garantie.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Client avec recherche */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <User className="h-4 w-4" /> Informations Client
              </h3>
              
              {/* Recherche client existant */}
              <div className="relative">
                <Label>Rechercher un client existant</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Rechercher par nom, téléphone..."
                    className="pl-10"
                  />
                  {loadingClients && (
                    <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />
                  )}
                </div>
                
                {/* Dropdown résultats */}
                {showClientDropdown && clientResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {clientResults.map((client, idx) => (
                      <div 
                        key={idx}
                        className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => handleSelectClient(client)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{client.name}</div>
                            <div className="text-sm text-slate-500">{client.phone} {client.email && `- ${client.email}`}</div>
                          </div>
                          <Badge variant="outline" className="text-xs">{client.source_label}</Badge>
                        </div>
                        {client.purchases?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <div className="text-xs text-slate-500">Achats récents :</div>
                            {client.purchases.slice(0, 3).map((p, i) => (
                              <div 
                                key={i} 
                                className="text-xs bg-slate-100 rounded px-2 py-1 flex justify-between items-center hover:bg-blue-50"
                                onClick={(e) => { e.stopPropagation(); handleSelectClient(client); handleImportPurchaseDate(p); }}
                              >
                                <span>{p.description?.substring(0, 40)}...</span>
                                <span className="text-slate-600">{p.date?.split('T')[0]}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Champs client */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={newDossier.client_name}
                    onChange={(e) => setNewDossier({...newDossier, client_name: e.target.value})}
                    placeholder="Nom du client"
                  />
                </div>
                <div>
                  <Label>Téléphone *</Label>
                  <Input
                    value={newDossier.client_phone}
                    onChange={(e) => setNewDossier({...newDossier, client_phone: e.target.value})}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newDossier.client_email}
                    onChange={(e) => setNewDossier({...newDossier, client_email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
              </div>
              
              {/* Achats du client sélectionné */}
              {selectedClient?.purchases?.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm font-medium text-blue-800 mb-2">Historique d'achats - Cliquez pour importer la date</div>
                  <div className="space-y-2">
                    {selectedClient.purchases.map((purchase, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between p-2 bg-white rounded border cursor-pointer hover:border-blue-400"
                        onClick={() => handleImportPurchaseDate(purchase)}
                      >
                        <div>
                          <div className="text-sm font-medium">{purchase.description?.substring(0, 50)}</div>
                          <div className="text-xs text-slate-500">{purchase.source} - {purchase.date?.split('T')[0]}</div>
                        </div>
                        <div className="text-sm font-medium text-green-600">{purchase.total?.toFixed(2)} €</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Produit */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Package className="h-4 w-4" /> Produit
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de garantie *</Label>
                  <Select 
                    value={newDossier.product_type} 
                    onValueChange={(v) => setNewDossier({...newDossier, product_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(WARRANTY_TYPES).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          {info.label} ({info.months} mois)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date d'achat {newDossier.purchase_source !== 'manual' && <span className="text-xs text-green-600">(auto)</span>}</Label>
                  <Input
                    type="date"
                    value={newDossier.purchase_date}
                    onChange={(e) => setNewDossier({...newDossier, purchase_date: e.target.value, purchase_source: 'manual'})}
                  />
                  {newDossier.purchase_source && newDossier.purchase_source !== 'manual' && (
                    <div className="text-xs text-green-600 mt-1">Importé depuis: {newDossier.purchase_source}</div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Description du produit *</Label>
                  <Input
                    value={newDossier.product_description}
                    onChange={(e) => setNewDossier({...newDossier, product_description: e.target.value})}
                    placeholder="Ex: iPhone 13 Pro Max - Écran remplacé"
                  />
                </div>
                <div>
                  <Label>N° Série</Label>
                  <Input
                    value={newDossier.serial_number}
                    onChange={(e) => setNewDossier({...newDossier, serial_number: e.target.value})}
                    placeholder="Optionnel"
                  />
                </div>
                <div>
                  <Label>IMEI</Label>
                  <Input
                    value={newDossier.imei}
                    onChange={(e) => setNewDossier({...newDossier, imei: e.target.value})}
                    placeholder="Optionnel"
                  />
                </div>
              </div>
            </div>

            {/* Type de réparation */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="h-4 w-4" /> Type de réparation & Délai
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de réparation</Label>
                  <Select 
                    value={newDossier.repair_type} 
                    onValueChange={(v) => setNewDossier({...newDossier, repair_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(REPAIR_TYPES).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          {info.label} - {info.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {newDossier.repair_type === 'personnalise' && (
                  <>
                    <div>
                      <Label>Délai personnalisé</Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Input
                            type="number"
                            min="0"
                            value={newDossier.custom_delay_days || ''}
                            onChange={(e) => setNewDossier({...newDossier, custom_delay_days: parseInt(e.target.value) || null})}
                            placeholder="Jours"
                          />
                          <span className="text-xs text-slate-500">jours</span>
                        </div>
                        <div className="flex-1">
                          <Input
                            type="number"
                            min="0"
                            value={newDossier.custom_delay_weeks || ''}
                            onChange={(e) => setNewDossier({...newDossier, custom_delay_weeks: parseInt(e.target.value) || null})}
                            placeholder="Semaines"
                          />
                          <span className="text-xs text-slate-500">semaines</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Problème */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> Problème signalé
              </h3>
              <div>
                <Label>Description du problème *</Label>
                <Textarea
                  value={newDossier.problem_description}
                  onChange={(e) => setNewDossier({...newDossier, problem_description: e.target.value})}
                  placeholder="Décrivez le problème rencontré par le client..."
                  rows={3}
                />
              </div>
              <div>
                <Label>État à l'entrée (visible)</Label>
                <Textarea
                  value={newDossier.entry_condition}
                  onChange={(e) => setNewDossier({...newDossier, entry_condition: e.target.value})}
                  placeholder="Ex: Écran intact, pas de rayures, sticker garantie présent..."
                  rows={2}
                />
              </div>
              <div>
                <Label>Type de réparation estimé</Label>
                <Select
                  value={newDossier.repair_type}
                  onValueChange={(v) => setNewDossier({...newDossier, repair_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (20min-4h si stock)</SelectItem>
                    <SelectItem value="commande">Sur commande (3-5 jours)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Checklist d'acceptation */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <CheckSquare className="h-4 w-4" /> Checklist d'acceptation (obligatoire)
              </h3>
              <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="check-sticker"
                    checked={newDossier.checklist_completed?.sticker_intact || false}
                    onCheckedChange={(v) => setNewDossier({
                      ...newDossier,
                      checklist_completed: {...(newDossier.checklist_completed || {}), sticker_intact: v}
                    })}
                  />
                  <Label htmlFor="check-sticker" className="cursor-pointer">Sticker de garantie intact et non altéré</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="check-choc"
                    checked={newDossier.checklist_completed?.no_choc || false}
                    onCheckedChange={(v) => setNewDossier({
                      ...newDossier,
                      checklist_completed: {...(newDossier.checklist_completed || {}), no_choc: v}
                    })}
                  />
                  <Label htmlFor="check-choc" className="cursor-pointer">Absence de traces de choc (vitre, châssis)</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="check-liquide"
                    checked={newDossier.checklist_completed?.no_liquide || false}
                    onCheckedChange={(v) => setNewDossier({
                      ...newDossier,
                      checklist_completed: {...(newDossier.checklist_completed || {}), no_liquide: v}
                    })}
                  />
                  <Label htmlFor="check-liquide" className="cursor-pointer">Absence de traces de liquide/oxydation</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="check-garantie"
                    checked={newDossier.checklist_completed?.garantie_active || false}
                    onCheckedChange={(v) => setNewDossier({
                      ...newDossier,
                      checklist_completed: {...(newDossier.checklist_completed || {}), garantie_active: v}
                    })}
                  />
                  <Label htmlFor="check-garantie" className="cursor-pointer">Période de garantie active</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="check-esthetique"
                    checked={newDossier.checklist_completed?.etat_esthetique || false}
                    onCheckedChange={(v) => setNewDossier({
                      ...newDossier,
                      checklist_completed: {...(newDossier.checklist_completed || {}), etat_esthetique: v}
                    })}
                  />
                  <Label htmlFor="check-esthetique" className="cursor-pointer">État esthétique conforme à l'achat initial</Label>
                </div>
                <div className="pt-2 border-t flex items-center gap-3">
                  <Checkbox
                    id="check-backup"
                    checked={newDossier.data_backup_confirmed}
                    onCheckedChange={(v) => setNewDossier({...newDossier, data_backup_confirmed: v})}
                  />
                  <Label htmlFor="check-backup" className="cursor-pointer font-medium text-amber-700">
                    ⚠️ Le client confirme avoir sauvegardé ses données
                  </Label>
                </div>
              </div>
            </div>

            {/* ========== CAHIER DES CHARGES COMPLET SAV & GARANTIES ========== */}
            <div className="space-y-4 border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <h3 className="font-bold text-blue-900 text-lg">📋 CAHIER DES CHARGES SAV & GARANTIES (ÉDITION COMPLÈTE)</h3>
              
              {/* 1. Référentiel des garanties */}
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <h4 className="font-bold text-green-800 mb-2">1. RÉFÉRENTIEL DES GARANTIES (AUTOMATISATION)</h4>
                <div className="text-xs text-green-700">Le système attribue ces délais par défaut :</div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-green-700">24 mois</span> - Appareils Neufs / Reconditionnés
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-green-700">12 mois</span> - Pièces d'Origine (Service Pack)
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-green-700">6 mois</span> - Compatibles Premium
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-green-700">3 mois</span> - Entrée de Gamme (minimum)
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-2 font-medium">
                  ⚖️ <strong>Règle Art. L217-11 :</strong> Immobilisation &gt; 7 jours = Extension automatique de la garantie.
                </p>
              </div>

              {/* 2. Ce qui est pris en charge */}
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <h4 className="font-bold text-emerald-800 mb-2">2. CE QUI EST PRIS EN CHARGE (GARANTIE VALIDE)</h4>
                <p className="text-xs text-emerald-700 mb-2">La garantie couvre exclusivement les vices de fabrication et défauts internes de la pièce posée :</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-emerald-700">📱 Affichage</span>
                    <ul className="text-emerald-600 list-disc list-inside">
                      <li>Pixels morts (sans trace de choc)</li>
                      <li>Scintillement anormal</li>
                      <li>Couleurs délavées subitement</li>
                    </ul>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-emerald-700">👆 Tactile</span>
                    <ul className="text-emerald-600 list-disc list-inside">
                      <li>Zones mortes sélectives</li>
                      <li>Tactile "fou" (fantôme) sans fissure</li>
                    </ul>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-emerald-700">🔋 Batterie</span>
                    <ul className="text-emerald-600 list-disc list-inside">
                      <li>Gonflement prématuré</li>
                      <li>Perte de capacité brutale (hors usure normale)</li>
                      <li>Défaut de charge</li>
                    </ul>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold text-emerald-700">⚙️ Composants</span>
                    <ul className="text-emerald-600 list-disc list-inside">
                      <li>Boutons physiques qui ne répondent plus</li>
                      <li>Nappe qui lâche sans manipulation</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 3. Ce qui est exclu */}
              <div className="bg-red-50 border-2 border-red-300 rounded p-3">
                <h4 className="font-bold text-red-800 mb-2">3. CE QUI EST EXCLU (REJET) - PREUVES PHOTO OBLIGATOIRES</h4>
                
                <div className="space-y-3">
                  {/* A. Dommages Écran */}
                  <div className="bg-white p-2 rounded border-l-4 border-red-400">
                    <span className="font-bold text-red-700 text-xs">A. Dommages sur Écran (Signes de choc/pression)</span>
                    <ul className="text-xs text-red-600 list-disc list-inside mt-1">
                      <li><strong>Lignes verticales/horizontales colorées :</strong> Rupture des micro-connexions LCD/OLED (chute/torsion)</li>
                      <li><strong>Taches noires (Cristaux liquides) :</strong> Impact direct ou pression excessive</li>
                      <li><strong>Écran blanc/noir total :</strong> Micro-fissures visibles sous lampe UV = choc interne</li>
                      <li><strong>Fissure "Cheveu" :</strong> Fissure fine partant d'un bord = torsion du châssis</li>
                    </ul>
                  </div>

                  {/* B. Dommages Physiques */}
                  <div className="bg-white p-2 rounded border-l-4 border-red-400">
                    <span className="font-bold text-red-700 text-xs">B. Dommages Physiques et Externes</span>
                    <ul className="text-xs text-red-600 list-disc list-inside mt-1">
                      <li><strong>Vitre brisée ou ébréchée :</strong> Tout éclat, même minime, annule la garantie écran</li>
                      <li><strong>Châssis tordu (Bent) :</strong> Téléphone non plat = tension permanente sur nappes/écran</li>
                      <li><strong>Coins enfoncés :</strong> Preuve directe de chute</li>
                    </ul>
                  </div>

                  {/* C. Intervention Tierce */}
                  <div className="bg-white p-2 rounded border-l-4 border-red-400">
                    <span className="font-bold text-red-700 text-xs">C. Intervention Tierce (Art. 1103 & 1231-1 Code Civil)</span>
                    <ul className="text-xs text-red-600 list-disc list-inside mt-1">
                      <li><strong>Vis marquées ou manquantes :</strong> Signe de démontage hors atelier</li>
                      <li><strong>Sticker/Sceau rompu :</strong> Altération = rejet automatique</li>
                      <li><strong>Pièce démontée :</strong> Toute pièce ramenée seule (hors appareil) est refusée</li>
                    </ul>
                  </div>

                  {/* D. Oxydation */}
                  <div className="bg-white p-2 rounded border-l-4 border-red-400">
                    <span className="font-bold text-red-700 text-xs">D. Oxydation et Usage</span>
                    <ul className="text-xs text-red-600 list-disc list-inside mt-1">
                      <li><strong>Pastilles d'humidité :</strong> Témoins blancs devenus rouges ou roses</li>
                      <li><strong>Traces calcaire/corrosion :</strong> Vert-de-gris sur les connecteurs</li>
                      <li><strong>Pins tordus :</strong> Dans le port de charge (mauvaise insertion câble)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 4. Clauses Juridiques */}
              <div className="bg-slate-100 border border-slate-300 rounded p-3">
                <h4 className="font-bold text-slate-800 mb-2">4. CLAUSES JURIDIQUES À INTÉGRER</h4>
                <ul className="text-xs text-slate-700 space-y-2">
                  <li><strong>📜 Exclusivité Technique :</strong> "La garantie est caduque en cas d'ouverture par un tiers, de choc, d'oxydation ou de mauvaise utilisation. La pièce doit rester montée dans l'appareil pour expertise."</li>
                  <li><strong>📜 Responsabilité Données :</strong> "Le client est responsable de la sauvegarde de ses données avant dépôt (Art. 1231-1 Code Civil)."</li>
                  <li><strong>📜 Droit de Rétention :</strong> "L'appareil reste en possession du réparateur jusqu'au paiement intégral (Art. 2286 Code Civil)."</li>
                </ul>
              </div>

              {/* 5. Interface Développeur */}
              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <h4 className="font-bold text-purple-800 mb-2">5. INTERFACE DÉVELOPPEUR (LOGIQUE SYSTÈME)</h4>
                <ul className="text-xs text-purple-700 space-y-1">
                  <li><strong>📸 Module Photo :</strong> Obligation de prendre 3 photos (Face, Dos, Profil) à l'entrée et à la sortie.</li>
                  <li><strong>💰 Mention Dynamique :</strong> "Le prix varie selon la largeur des produits et quantités. Rapport qualité-prix et rapidité garantis."</li>
                  <li><strong>✍️ Signature :</strong> Signature électronique obligatoire à chaque étape pour valider l'état fonctionnel.</li>
                </ul>
              </div>

              {/* Délais opérationnels */}
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <h4 className="font-semibold text-amber-800 mb-2">⏱️ DÉLAIS OPÉRATIONNELS</h4>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li><strong>Diagnostic :</strong> 24h à 48h ouvrées</li>
                  <li><strong>Réparation standard :</strong> 20 min à 4h (si stock)</li>
                  <li><strong>Validation SAV (Tests qualité) :</strong> 7 à 10 jours ouvrés</li>
                  <li><strong>Attente pièce :</strong> 3 à 5 jours ouvrés</li>
                  <li><strong>⚠️ Non-récupération :</strong> Appareil non récupéré sous 90 jours devient propriété de la boutique</li>
                </ul>
              </div>
              
              {/* Mention légale complète */}
              <div className="bg-slate-100 border border-slate-300 rounded p-3">
                <h4 className="font-semibold text-slate-800 mb-2">📋 MENTION LÉGALE</h4>
                <p className="text-xs text-slate-700">{config?.legal_mention}</p>
              </div>
              
              {/* Exonération données */}
              <div className="bg-orange-100 border-2 border-orange-400 rounded p-3">
                <h4 className="font-bold text-orange-800 mb-1">⚠️ EXONÉRATION DE RESPONSABILITÉ - DONNÉES</h4>
                <p className="text-sm text-orange-700">La boutique n'est pas responsable des données personnelles. Le client doit impérativement effectuer une sauvegarde AVANT le dépôt.</p>
              </div>
            </div>

            {/* Signature d'entrée */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <PenLine className="h-4 w-4" /> Signature d'entrée (état des lieux + acceptation des risques)
              </h3>
              
              {/* Texte d'acceptation avant signature */}
              <div className="p-3 bg-slate-100 rounded-lg text-xs text-slate-700">
                <p className="font-medium mb-2">En signant, le client confirme :</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Avoir sauvegardé ses données personnelles (photos, contacts, etc.)</li>
                  <li>Avoir pris connaissance que la boutique n'est pas responsable de la perte de données</li>
                  <li>Avoir décrit l'état esthétique actuel de son appareil</li>
                  <li>Accepter les conditions générales de prise en charge SAV</li>
                </ol>
              </div>

              {newDossier.entry_signature ? (
                <div className="p-3 border rounded-lg bg-green-50 border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-4 w-4" />
                    Signature enregistrée
                  </div>
                  <img src={newDossier.entry_signature} alt="Signature" className="mt-2 max-h-20 border rounded" />
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Option 1 : Signature sur place</p>
                  <SignatureCanvas
                    onSave={(sig) => setNewDossier({...newDossier, entry_signature: sig})}
                    onClear={() => setNewDossier({...newDossier, entry_signature: null})}
                  />
                  
                  <div className="flex items-center gap-3 py-2">
                    <div className="flex-1 border-t border-slate-300"></div>
                    <span className="text-sm text-slate-500 font-medium">OU</span>
                    <div className="flex-1 border-t border-slate-300"></div>
                  </div>
                  
                  <div className="p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
                    <p className="text-sm font-medium text-purple-800 mb-3">
                      <Send className="h-4 w-4 inline mr-2" />
                      Option 2 : Signature à distance par SMS
                    </p>
                    <p className="text-xs text-purple-600 mb-3">
                      Le client reçoit un SMS avec un lien sécurisé pour signer sur son téléphone.
                    </p>
                    <Button 
                      type="button"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={async () => {
                        if (!newDossier.client_name || !newDossier.client_phone || !newDossier.product_description || !newDossier.problem_description) {
                          toast.error('Veuillez remplir tous les champs obligatoires avant d\'envoyer le lien');
                          return;
                        }
                        // Créer le dossier sans signature
                        const result = await handleCreateDossier();
                        if (result && result.dossier_id) {
                          try {
                            await axios.post(`${API}/api/sav/${result.dossier_id}/remote-signature/send`, {
                              signature_type: 'entry',
                              send_method: 'sms'
                            }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});
                            toast.success('Dossier créé et lien de signature envoyé par SMS !');
                          } catch (err) {
                            toast.error('Dossier créé mais erreur lors de l\'envoi du lien');
                          }
                        }
                      }}
                      data-testid="create-with-remote-signature-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Créer + Envoyer lien signature SMS
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label>Notes internes</Label>
              <Textarea
                value={newDossier.notes}
                onChange={(e) => setNewDossier({...newDossier, notes: e.target.value})}
                placeholder="Notes pour l'équipe technique..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateDossier}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-dossier-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Créer le dossier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Détail */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedDossier && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Dossier {selectedDossier.id}
                  </DialogTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        refreshDossier(selectedDossier.id);
                        setPollingDossierId(selectedDossier.id);
                      }}
                      className="gap-1"
                      data-testid="refresh-dossier-btn"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Actualiser
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={STATUS_COLORS[selectedDossier.status]}>
                    {STATUS_LABELS[selectedDossier.status]}
                  </Badge>
                  {pollingDossierId === selectedDossier.id && (
                    <Badge variant="outline" className="animate-pulse bg-blue-50 text-blue-700">
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Détection signature en cours...
                    </Badge>
                  )}
                </div>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Client */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <User className="h-4 w-4" /> Client
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Nom:</span> {selectedDossier.client_name}</div>
                    <div><span className="text-slate-500">Tél:</span> {selectedDossier.client_phone}</div>
                    {selectedDossier.client_email && (
                      <div className="col-span-2"><span className="text-slate-500">Email:</span> {selectedDossier.client_email}</div>
                    )}
                  </div>
                </div>

                {/* Produit */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Package className="h-4 w-4" /> Produit
                  </h4>
                  <div className="text-sm space-y-1">
                    <div>{selectedDossier.product_description}</div>
                    <div className="text-slate-500">
                      Garantie: {WARRANTY_TYPES[selectedDossier.product_type]?.label}
                    </div>
                    {selectedDossier.warranty_info && (
                      <div className={selectedDossier.warranty_info.is_valid ? 'text-green-600' : 'text-red-600'}>
                        {selectedDossier.warranty_info.is_valid 
                          ? `${selectedDossier.warranty_info.days_remaining} jours restants`
                          : 'Garantie expirée'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Problème */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> Problème
                  </h4>
                  <p className="text-sm">{selectedDossier.problem_description}</p>
                  {selectedDossier.entry_condition && (
                    <p className="text-sm text-slate-500 mt-1">État à l'entrée: {selectedDossier.entry_condition}</p>
                  )}
                </div>

                {/* Conditions de garantie */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-amber-800">
                    <Shield className="h-4 w-4" /> Conditions de Garantie
                  </h4>
                  <div className="text-xs text-amber-700 space-y-2">
                    <div className="font-medium">Délais de garantie:</div>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Appareils Origine: 24 mois</li>
                      <li>Pièces Origine: 12 mois</li>
                      <li>Compatible Premium: 6 mois</li>
                      <li>Entrée de gamme: 3 mois</li>
                    </ul>
                    <div className="font-medium mt-2">Clause d'exclusivité technique:</div>
                    <p className="text-red-700">⚠️ Toute intervention hors de l'établissement entraîne la nullité immédiate de la garantie.</p>
                    <ul className="list-disc list-inside space-y-1 text-red-600">
                      <li>Interdiction d'ouverture par le client ou tiers</li>
                      <li>La pièce doit rester montée dans l'appareil</li>
                      <li>Sticker de garantie doit rester intact</li>
                    </ul>
                    <div className="mt-2 text-slate-600 italic">
                      Base: Art. 1103 et 1231-1 du Code Civil
                    </div>
                  </div>
                </div>

                {/* Checklist validée */}
                {selectedDossier.checklist_completed && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <CheckSquare className="h-4 w-4" /> Checklist validée à l'entrée
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={selectedDossier.checklist_completed.sticker_intact ? 'text-green-600' : 'text-red-600'}>
                        {selectedDossier.checklist_completed.sticker_intact ? '✓' : '✗'} Sticker intact
                      </div>
                      <div className={selectedDossier.checklist_completed.no_choc ? 'text-green-600' : 'text-red-600'}>
                        {selectedDossier.checklist_completed.no_choc ? '✓' : '✗'} Pas de choc
                      </div>
                      <div className={selectedDossier.checklist_completed.no_liquide ? 'text-green-600' : 'text-red-600'}>
                        {selectedDossier.checklist_completed.no_liquide ? '✓' : '✗'} Pas d'oxydation
                      </div>
                      <div className={selectedDossier.checklist_completed.garantie_active ? 'text-green-600' : 'text-red-600'}>
                        {selectedDossier.checklist_completed.garantie_active ? '✓' : '✗'} Garantie active
                      </div>
                      <div className={selectedDossier.checklist_completed.etat_esthetique ? 'text-green-600' : 'text-red-600'}>
                        {selectedDossier.checklist_completed.etat_esthetique ? '✓' : '✗'} État esthétique OK
                      </div>
                      <div className={selectedDossier.data_backup_confirmed ? 'text-green-600' : 'text-red-600'}>
                        {selectedDossier.data_backup_confirmed ? '✓' : '✗'} Données sauvegardées
                      </div>
                    </div>
                  </div>
                )}

                {/* Historique statuts */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <History className="h-4 w-4" /> Historique
                  </h4>
                  <div className="space-y-2">
                    {selectedDossier.status_history?.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <ChevronRight className="h-3 w-3 text-slate-400" />
                        <Badge variant="outline" className="text-xs">{STATUS_LABELS[h.status]}</Badge>
                        <span className="text-slate-500">
                          {new Date(h.timestamp).toLocaleString('fr-FR')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions rapides */}
                <div className="flex gap-2 flex-wrap">
                  {selectedDossier.status !== 'cloture' && selectedDossier.status !== 'rejete' && (
                    <>
                      <Select onValueChange={(v) => handleStatusChange(selectedDossier.id, v)}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Changer statut" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_LABELS).filter(([k]) => k !== selectedDossier.status && k !== 'rejete' && k !== 'cloture').map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* NOUVEAU: Bouton Conclure le dossier */}
                      <Button 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => { setConclusionForm(initialConclusionState); setShowDetailModal(false); setShowConclusionModal(true); }}
                        data-testid="conclude-dossier-btn"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" /> Conclure
                      </Button>
                      
                      <Button 
                        variant="destructive" 
                        onClick={() => { setShowDetailModal(false); setShowRejectModal(true); }}
                        data-testid="reject-dossier-btn"
                      >
                        <Ban className="h-4 w-4 mr-1" /> Rejeter (ancien)
                      </Button>
                    </>
                  )}
                  {selectedDossier.status === 'pret' && (
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => { setSignatureType('exit'); setShowDetailModal(false); setShowSignatureModal(true); }}
                      data-testid="exit-signature-btn"
                    >
                      <PenLine className="h-4 w-4 mr-1" /> Signature sortie & Clôturer
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => { setShowDetailModal(false); setShowMessageModal(true); }}>
                    <MessageSquare className="h-4 w-4 mr-1" /> Message manuel
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowDetailModal(false); setShowNotifyModal(true); }}
                    className="gap-1"
                    data-testid="manual-notify-btn"
                  >
                    <Bell className="h-4 w-4" /> Notifier statut
                  </Button>
                </div>
                
                {/* Signature à distance */}
                <div className="flex gap-2 flex-wrap border-t pt-3 mt-3">
                  <span className="text-sm text-slate-500 w-full mb-1">📱 Signature à distance :</span>
                  {!selectedDossier.entry_signature && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSendRemoteSignatureLink(selectedDossier.id, 'entry')}
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      <Send className="h-4 w-4 mr-1" /> Lien signature entrée
                    </Button>
                  )}
                  {selectedDossier.status === 'pret' && !selectedDossier.exit_signature && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSendRemoteSignatureLink(selectedDossier.id, 'exit')}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <Send className="h-4 w-4 mr-1" /> Lien signature sortie
                    </Button>
                  )}
                  {selectedDossier.entry_signature && (
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle className="h-3 w-3 mr-1" /> Entrée signée
                    </Badge>
                  )}
                  {selectedDossier.exit_signature && (
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle className="h-3 w-3 mr-1" /> Sortie signée
                    </Badge>
                  )}
                </div>

                {/* Actions secondaires */}
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleLoadPrintData(selectedDossier.id)}
                    data-testid="print-dossier-btn"
                  >
                    <FileText className="h-4 w-4 mr-1" /> Imprimer / Envoyer fiche
                  </Button>
                  
                  {(selectedDossier.status === 'cloture' || selectedDossier.status === 'rejete') && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setShowDetailModal(false); setShowReopenModal(true); }}
                      data-testid="reopen-dossier-btn"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" /> Réouvrir
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteDossier(selectedDossier.id)}
                    data-testid="delete-dossier-btn"
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Supprimer
                  </Button>
                </div>

                {/* Info rejet si rejeté */}
                {selectedDossier.status === 'rejete' && selectedDossier.rejection_info && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-semibold mb-2 text-red-700 flex items-center gap-2">
                      <Ban className="h-4 w-4" /> Dossier rejeté
                    </h4>
                    <div className="text-sm space-y-1">
                      <div><strong>Motif:</strong> {selectedDossier.rejection_info.reason_label}</div>
                      <div className="text-red-600">{selectedDossier.rejection_info.reason_description}</div>
                      <div><strong>Notes:</strong> {selectedDossier.rejection_info.notes}</div>
                      <div className="text-xs text-red-500">
                        Rejeté le: {new Date(selectedDossier.rejection_info.rejected_at).toLocaleString('fr-FR')}
                      </div>
                      {selectedDossier.rejection_info.photos?.length > 0 && (
                        <div className="mt-2">
                          <strong>Photos de preuve:</strong>
                          <div className="flex gap-2 mt-1">
                            {selectedDossier.rejection_info.photos.map((photo, i) => (
                              <img key={i} src={photo} alt={`Preuve ${i+1}`} className="h-20 w-20 object-cover rounded border" />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* NOUVELLE SECTION: Affichage de la Conclusion */}
                {selectedDossier.conclusion && (
                  <div className={`p-4 rounded-lg border-2 ${
                    selectedDossier.conclusion.decision_category === 'accepted' 
                      ? 'bg-green-50 border-green-300' 
                      : 'bg-amber-50 border-amber-300'
                  }`}>
                    <h4 className={`font-semibold mb-3 flex items-center gap-2 ${
                      selectedDossier.conclusion.decision_category === 'accepted' ? 'text-green-800' : 'text-amber-800'
                    }`}>
                      <CheckCircle className="h-5 w-5" />
                      Conclusion du dossier
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{selectedDossier.conclusion.decision_icon}</span>
                        <div>
                          <div className="font-semibold text-lg">{selectedDossier.conclusion.decision_label}</div>
                          <div className="text-sm text-slate-600">{selectedDossier.conclusion.description}</div>
                        </div>
                      </div>
                      
                      {/* Détails financiers */}
                      {selectedDossier.conclusion.amount && (
                        <div className="flex items-center gap-2 p-2 bg-white rounded border">
                          <span className="font-medium">Montant:</span>
                          <span className="text-lg font-bold text-green-600">{selectedDossier.conclusion.amount.toFixed(2)} €</span>
                          {selectedDossier.conclusion.percentage && (
                            <Badge variant="outline">({selectedDossier.conclusion.percentage}%)</Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Soulte/Supplément */}
                      {selectedDossier.conclusion.supplement_amount && (
                        <div className="flex items-center gap-2 p-2 bg-white rounded border">
                          <span className="font-medium">Supplément à régler:</span>
                          <span className="text-lg font-bold text-orange-600">{selectedDossier.conclusion.supplement_amount.toFixed(2)} €</span>
                        </div>
                      )}
                      
                      {/* Nouveau produit */}
                      {selectedDossier.conclusion.new_product_description && (
                        <div className="p-2 bg-white rounded border">
                          <span className="font-medium">Nouveau produit:</span>
                          <span className="ml-2">{selectedDossier.conclusion.new_product_description}</span>
                        </div>
                      )}
                      
                      {/* Validité avoir */}
                      {selectedDossier.conclusion.avoir_validity_date && (
                        <div className="p-2 bg-blue-100 rounded border border-blue-200">
                          <span className="font-medium text-blue-800">Avoir valable jusqu'au:</span>
                          <span className="ml-2 text-blue-700">{new Date(selectedDossier.conclusion.avoir_validity_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                      
                      {/* Référence devis */}
                      {selectedDossier.conclusion.quote_reference && (
                        <div className="p-2 bg-white rounded border">
                          <span className="font-medium">Référence devis:</span>
                          <span className="ml-2 font-mono">{selectedDossier.conclusion.quote_reference}</span>
                        </div>
                      )}
                      
                      {/* Notes technicien */}
                      {selectedDossier.conclusion.technician_notes && (
                        <div className="p-2 bg-slate-100 rounded border text-sm">
                          <span className="font-medium">Notes technicien:</span>
                          <p className="mt-1 text-slate-700">{selectedDossier.conclusion.technician_notes}</p>
                        </div>
                      )}
                      
                      <div className="text-xs text-slate-500 pt-2 border-t">
                        Conclu le: {new Date(selectedDossier.conclusion.concluded_at).toLocaleString('fr-FR')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Signatures - TOUJOURS AFFICHER */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <PenLine className="h-4 w-4" /> Signatures
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedDossier.entry_signature && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="text-xs font-semibold text-green-700 mb-1">✅ Signature Entrée</div>
                          <div className="text-xs text-slate-600 mb-2">
                            📅 {selectedDossier.entry_signature_date && new Date(selectedDossier.entry_signature_date).toLocaleString('fr-FR')}
                            <br/>
                            📱 Méthode : {selectedDossier.entry_signature_remote ? 'À distance (SMS/OTP)' : 'Sur place'}
                          </div>
                          <img src={selectedDossier.entry_signature} alt="Signature entrée" className="h-16 border rounded bg-white" />
                        </div>
                      )}
                      {!selectedDossier.entry_signature && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="text-xs font-semibold text-amber-700">⏳ En attente de signature d'entrée</div>
                          <p className="text-xs text-amber-600 mt-1">Le client n'a pas encore signé l'acceptation du dépôt.</p>
                        </div>
                      )}
                      {selectedDossier.exit_signature && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="text-xs font-semibold text-blue-700 mb-1">✅ Signature Sortie</div>
                          <div className="text-xs text-slate-600 mb-2">
                            📅 {selectedDossier.exit_signature_date && new Date(selectedDossier.exit_signature_date).toLocaleString('fr-FR')}
                            <br/>
                            📱 Méthode : {selectedDossier.exit_signature_remote ? 'À distance (SMS/OTP)' : 'Sur place'}
                          </div>
                          <img src={selectedDossier.exit_signature} alt="Signature sortie" className="h-16 border rounded bg-white" />
                        </div>
                      )}
                    </div>
                  </div>

                {/* Prolongation garantie si immobilisation > 7j */}
                {selectedDossier.immobilization_days > 7 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold mb-1 text-blue-700 flex items-center gap-2">
                      <Shield className="h-4 w-4" /> Prolongation de garantie (Art. L217-11)
                    </h4>
                    <div className="text-sm text-blue-600">
                      Immobilisation de {selectedDossier.immobilization_days} jours → Garantie prolongée de {selectedDossier.immobilization_days} jours
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Rejet (avec photo obligatoire) */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="h-5 w-5" />
              Rejeter le dossier
            </DialogTitle>
            {selectedDossier && (
              <DialogDescription>
                Dossier: {selectedDossier.id} - {selectedDossier.client_name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Motif de rejet *</Label>
              <Select value={rejectForm.rejection_reason} onValueChange={(v) => setRejectForm({...rejectForm, rejection_reason: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le motif" />
                </SelectTrigger>
                <SelectContent>
                  {config?.rejection_reasons && Object.entries(config.rejection_reasons).map(([key, info]) => (
                    <SelectItem key={key} value={key}>
                      {info.label} {info.requires_photo && '📷'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {rejectForm.rejection_reason && config?.rejection_reasons?.[rejectForm.rejection_reason] && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <div className="font-medium text-red-700">{config.rejection_reasons[rejectForm.rejection_reason].label}</div>
                <div className="text-red-600 mt-1">{config.rejection_reasons[rejectForm.rejection_reason].description}</div>
                {config.rejection_reasons[rejectForm.rejection_reason].requires_photo && (
                  <div className="mt-2 text-amber-700 font-medium flex items-center gap-1">
                    <Camera className="h-4 w-4" /> Photo de preuve OBLIGATOIRE
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Notes de rejet *</Label>
              <Textarea
                value={rejectForm.rejection_notes}
                onChange={(e) => setRejectForm({...rejectForm, rejection_notes: e.target.value})}
                placeholder="Détails du constat (visible par le client)..."
                rows={3}
              />
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Camera className="h-4 w-4" /> Photos de preuve {config?.rejection_reasons?.[rejectForm.rejection_reason]?.requires_photo && <span className="text-red-600">*</span>}
              </Label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="reject-photo-upload"
                />
                <label
                  htmlFor="reject-photo-upload"
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50"
                >
                  <Upload className="h-4 w-4" />
                  Ajouter une photo
                </label>
              </div>
              {rejectForm.rejection_photos.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {rejectForm.rejection_photos.map((photo, i) => (
                    <div key={i} className="relative">
                      <img src={photo} alt={`Photo ${i+1}`} className="h-20 w-20 object-cover rounded border" />
                      <button
                        type="button"
                        onClick={() => setRejectForm({...rejectForm, rejection_photos: rejectForm.rejection_photos.filter((_, idx) => idx !== i)})}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleRejectDossier}>
              <Ban className="h-4 w-4 mr-1" />
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Signature de sortie */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <PenLine className="h-5 w-5" />
              {signatureType === 'entry' ? 'Signature d\'entrée' : 'Signature de sortie - Bon pour livraison'}
            </DialogTitle>
            {selectedDossier && (
              <DialogDescription>
                Client: {selectedDossier.client_name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {signatureType === 'exit' && (
              <div className="p-3 bg-slate-100 rounded-lg text-sm">
                <p className="font-medium mb-2">Texte de reconnaissance:</p>
                <p className="text-slate-600 italic text-xs">
                  "Je, soussigné(e) <strong>{selectedDossier?.client_name}</strong>, certifie avoir testé mon appareil et constaté la réparation effectuée. 
                  Je reconnais avoir pris connaissance des délais de garantie (3 à 24 mois selon gamme) et accepte que tout choc, humidité ou ouverture par un tiers annule cette protection. 
                  Aucun retour ne sera accepté sans le présent justificatif et avec un sticker endommagé."
                </p>
              </div>
            )}

            <div>
              <Label>Signature du client</Label>
              <div className="mt-2">
                <SignatureCanvas
                  onSave={handleSaveSignature}
                  onClear={() => {}}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Message */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Envoyer un message
            </DialogTitle>
            {selectedDossier && (
              <DialogDescription>
                À: {selectedDossier.client_name} ({selectedDossier.client_phone})
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Type d'envoi</Label>
              <Select value={messageForm.message_type} onValueChange={(v) => setMessageForm({...messageForm, message_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS uniquement</SelectItem>
                  <SelectItem value="email">Email uniquement</SelectItem>
                  <SelectItem value="both">SMS + Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={messageForm.message}
                onChange={(e) => setMessageForm({...messageForm, message: e.target.value})}
                placeholder="Votre message au client..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSendMessage} className="bg-blue-600 hover:bg-blue-700">
              <Send className="h-4 w-4 mr-1" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Notification Manuelle du Statut */}
      <Dialog open={showNotifyModal} onOpenChange={setShowNotifyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              Notification Manuelle
            </DialogTitle>
            {selectedDossier && (
              <DialogDescription>
                Envoyer une notification au client pour le dossier {selectedDossier.id}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Sélectionnez le type de notification à envoyer au client (SMS + Email si configurés) :
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3 px-4"
                onClick={() => handleManualNotification('reception')}
              >
                <div className="text-left">
                  <div className="font-medium">📥 Réception confirmée</div>
                  <div className="text-xs text-slate-500">Confirmer la prise en charge de l'appareil</div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3 px-4"
                onClick={() => handleManualNotification('diagnostic')}
              >
                <div className="text-left">
                  <div className="font-medium">🔍 Diagnostic en cours</div>
                  <div className="text-xs text-slate-500">Informer que le diagnostic est lancé</div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3 px-4"
                onClick={() => handleManualNotification('en_cours')}
              >
                <div className="text-left">
                  <div className="font-medium">🔧 Réparation en cours</div>
                  <div className="text-xs text-slate-500">Informer que la réparation a commencé</div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3 px-4"
                onClick={() => handleManualNotification('attente_piece')}
              >
                <div className="text-left">
                  <div className="font-medium">📦 Attente de pièce</div>
                  <div className="text-xs text-slate-500">Informer d'un délai pour commande de pièce</div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3 px-4 border-green-300 hover:bg-green-50"
                onClick={() => handleManualNotification('pret')}
              >
                <div className="text-left">
                  <div className="font-medium text-green-700">✅ Appareil prêt</div>
                  <div className="text-xs text-green-600">Informer que l'appareil peut être récupéré</div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3 px-4"
                onClick={() => handleManualNotification('rappel')}
              >
                <div className="text-left">
                  <div className="font-medium">⏰ Rappel de récupération</div>
                  <div className="text-xs text-slate-500">Rappeler au client de venir récupérer</div>
                </div>
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotifyModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Réouverture */}
      <Dialog open={showReopenModal} onOpenChange={setShowReopenModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              Réouvrir le dossier
            </DialogTitle>
            {selectedDossier && (
              <DialogDescription>
                Réouvrir le dossier {selectedDossier.id} actuellement {STATUS_LABELS[selectedDossier.status]?.toLowerCase()}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Raison de la réouverture *</Label>
              <Textarea
                id="reopen-reason"
                placeholder="Ex: Client revient avec un nouveau problème, erreur de clôture..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Nouveau statut</Label>
              <Select defaultValue="en_attente">
                <SelectTrigger id="reopen-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="diagnostic">Diagnostic</SelectItem>
                  <SelectItem value="en_cours">En cours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReopenModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={() => {
                const reason = document.getElementById('reopen-reason')?.value || 'Réouverture';
                const status = document.getElementById('reopen-status')?.value || 'en_attente';
                handleReopenDossier(reason, status);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Réouvrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Impression / Envoi fiche SAV */}
      <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Fiche SAV - Impression / Envoi
            </DialogTitle>
          </DialogHeader>
          
          {printData && (
            <div className="space-y-4 py-4" id="print-content">
              {/* En-tête avec QR Code */}
              <div className="flex justify-between items-start border-b pb-4">
                <div>
                  <h2 className="text-xl font-bold">FICHE SAV</h2>
                  <div className="text-lg font-mono">{printData.dossier?.id}</div>
                  <div className="text-sm text-slate-500">
                    Créé le: {printData.dossier?.created_at && new Date(printData.dossier.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div className="text-center">
                  <img src={printData.qr_code} alt="QR Code" className="h-24 w-24" />
                  <div className="text-xs text-slate-500 mt-1">Scanner pour retrouver</div>
                </div>
              </div>

              {/* Client */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-slate-700">CLIENT</h3>
                  <div className="text-sm mt-1">
                    <div>{printData.dossier?.client_name}</div>
                    <div>{printData.dossier?.client_phone}</div>
                    {printData.dossier?.client_email && <div>{printData.dossier.client_email}</div>}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-700">APPAREIL</h3>
                  <div className="text-sm mt-1">
                    <div>{printData.dossier?.product_description}</div>
                    {printData.dossier?.serial_number && <div>N° Série: {printData.dossier.serial_number}</div>}
                    {printData.dossier?.imei && <div>IMEI: {printData.dossier.imei}</div>}
                  </div>
                </div>
              </div>

              {/* Garantie */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-800">GARANTIE</h3>
                <div className="text-sm mt-1">
                  <div>Type: {printData.warranty_info?.label} ({printData.warranty_info?.months} mois)</div>
                  <div>Date d'achat: {printData.dossier?.purchase_date?.split('T')[0] || 'N/A'}</div>
                  <div>Expire le: {printData.dossier?.warranty_info?.warranty_end?.split('T')[0] || 'N/A'}</div>
                </div>
              </div>

              {/* Problème */}
              <div>
                <h3 className="font-semibold text-slate-700">PROBLÈME SIGNALÉ</h3>
                <div className="text-sm mt-1 p-2 bg-slate-50 rounded">{printData.dossier?.problem_description}</div>
              </div>

              {/* ========== CAHIER DES CHARGES COMPLET IMPRESSION ========== */}
              
              {/* 1. Référentiel des garanties */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs">
                <h3 className="font-bold text-green-800 mb-2">1. RÉFÉRENTIEL DES GARANTIES</h3>
                <div className="grid grid-cols-2 gap-1">
                  <div>• Appareils Neufs/Reconditionnés: 24 mois</div>
                  <div>• Pièces Origine (Service Pack): 12 mois</div>
                  <div>• Compatibles Premium: 6 mois</div>
                  <div>• Entrée de Gamme: 3 mois min</div>
                </div>
                <p className="text-green-600 mt-1 font-medium">⚖️ Art. L217-11: Immobilisation &gt; 7j = Extension automatique</p>
              </div>

              {/* 2. Ce qui est pris en charge */}
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
                <h3 className="font-bold text-emerald-800 mb-2">2. CE QUI EST PRIS EN CHARGE (GARANTIE VALIDE)</h3>
                <p className="text-emerald-700 mb-1">Vices de fabrication et défauts internes uniquement:</p>
                <div className="grid grid-cols-2 gap-1 text-emerald-600">
                  <div>📱 Affichage: pixels morts (sans choc), scintillement, couleurs délavées</div>
                  <div>👆 Tactile: zones mortes, tactile "fou" sans fissure</div>
                  <div>🔋 Batterie: gonflement, perte capacité brutale, défaut charge</div>
                  <div>⚙️ Composants: boutons inopérants, nappe défaillante</div>
                </div>
              </div>

              {/* 3. Ce qui est exclu */}
              <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg text-xs">
                <h3 className="font-bold text-red-800 mb-2">3. CE QUI EST EXCLU (REJET) - PREUVE PHOTO OBLIGATOIRE</h3>
                <div className="space-y-1 text-red-600">
                  <div><strong>A. Écran:</strong> Lignes colorées, taches noires, écran blanc/noir, fissure "Cheveu"</div>
                  <div><strong>B. Physique:</strong> Vitre brisée/ébréchée, châssis tordu (Bent), coins enfoncés</div>
                  <div><strong>C. Intervention Tierce:</strong> Vis marquées/manquantes, sticker/sceau rompu, pièce démontée</div>
                  <div><strong>D. Oxydation:</strong> Pastilles rouges/roses, traces calcaire/vert-de-gris, pins tordus</div>
                </div>
              </div>

              {/* 4. Clauses Juridiques */}
              <div className="p-3 bg-slate-100 border border-slate-300 rounded-lg text-xs">
                <h3 className="font-bold text-slate-800 mb-2">4. CLAUSES JURIDIQUES</h3>
                <ul className="text-slate-700 space-y-1">
                  <li>📜 <strong>Exclusivité:</strong> Garantie caduque si ouverture par tiers, choc, oxydation. Pièce DOIT rester montée.</li>
                  <li>📜 <strong>Données (Art. 1231-1):</strong> Client responsable de la sauvegarde avant dépôt.</li>
                  <li>📜 <strong>Rétention (Art. 2286):</strong> Appareil conservé jusqu'au paiement intégral.</li>
                </ul>
              </div>

              {/* 5. Interface Développeur */}
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-xs">
                <h3 className="font-bold text-purple-800 mb-2">5. LOGIQUE SYSTÈME</h3>
                <ul className="text-purple-700 space-y-1">
                  <li>📸 3 photos obligatoires (Face, Dos, Profil) à l'entrée et sortie</li>
                  <li>💰 Le prix varie selon la largeur des produits et quantités</li>
                  <li>✍️ Signature électronique obligatoire à chaque étape</li>
                </ul>
              </div>

              {/* Mentions légales */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                <h3 className="font-semibold text-amber-800 mb-2">MENTION LÉGALE COMPLÈTE</h3>
                <p className="text-amber-700">{printData.legal_mention}</p>
              </div>

              {/* Clause d'exclusivité - détaillée */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs">
                <h3 className="font-semibold text-red-800 mb-2">⚠️ CLAUSE D'EXCLUSIVITÉ TECHNIQUE</h3>
                <p className="text-red-700 font-medium">{printData.exclusivity_clause?.rule}</p>
                <ul className="mt-2 space-y-1 text-red-600">
                  {printData.exclusivity_clause?.conditions?.map((c, i) => (
                    <li key={i}>• {c}</li>
                  ))}
                </ul>
                <div className="mt-2 text-slate-600">
                  Base juridique: {printData.exclusivity_clause?.legal_basis?.join(' | ')}
                </div>
              </div>

              {/* Exonération Données */}
              <div className="p-3 bg-orange-100 border-2 border-orange-400 rounded-lg text-xs">
                <h3 className="font-bold text-orange-800 mb-1">⚠️ EXONÉRATION DONNÉES</h3>
                <p className="text-orange-700">La boutique n'est pas responsable des données personnelles. Sauvegardez AVANT le dépôt.</p>
              </div>

              {/* Signature client */}
              <div className="border-t pt-4">
                <div className="text-xs text-slate-600 italic">{printData.exit_signature_text}</div>
                <div className="mt-4 flex justify-between items-end">
                  <div>
                    <div className="text-xs text-slate-500">Signature client:</div>
                    <div className="w-48 h-16 border-b border-slate-400 mt-1"></div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Date:</div>
                    <div className="w-32 h-8 border-b border-slate-400 mt-1"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleSendSheet(true, false)}>
                <Phone className="h-4 w-4 mr-1" /> Envoyer SMS
              </Button>
              <Button variant="outline" onClick={() => handleSendSheet(false, true)}>
                <Mail className="h-4 w-4 mr-1" /> Envoyer Email
              </Button>
            </div>
            <Button 
              onClick={() => {
                const content = document.getElementById('print-content');
                if (content) {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>Fiche SAV - ${printData?.dossier?.id}</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          .print-content { max-width: 800px; margin: 0 auto; }
                        </style>
                      </head>
                      <body>
                        <div class="print-content">${content.innerHTML}</div>
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.print();
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <FileText className="h-4 w-4 mr-1" /> Imprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Dossiers Supprimés */}
      <Dialog open={showDeletedModal} onOpenChange={setShowDeletedModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Dossiers supprimés (Historique)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {deletedDossiers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Aucun dossier supprimé
              </div>
            ) : (
              deletedDossiers.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div>
                    <div className="font-semibold">{d.id}</div>
                    <div className="text-sm text-slate-600">{d.client_name} - {d.product_description}</div>
                    <div className="text-xs text-slate-500">
                      Supprimé le: {d.deleted_at && new Date(d.deleted_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleRestoreDossier(d.id)}>
                      Restaurer
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteDossier(d.id, true)}>
                      Suppr. définitif
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeletedModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NOUVEAU MODAL: Conclusion de Dossier */}
      <Dialog open={showConclusionModal} onOpenChange={setShowConclusionModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              Conclure le dossier
            </DialogTitle>
            {selectedDossier && (
              <DialogDescription>
                Dossier {selectedDossier.id} - {selectedDossier.client_name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Sélection de la catégorie */}
            <Tabs defaultValue="accepted" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="accepted" className="text-green-700 data-[state=active]:bg-green-100">
                  ✅ Dossier Accepté
                </TabsTrigger>
                <TabsTrigger value="rejected" className="text-amber-700 data-[state=active]:bg-amber-100">
                  ⚠️ Dossier Rejeté
                </TabsTrigger>
              </TabsList>
              
              {/* Décisions Acceptées */}
              <TabsContent value="accepted" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(ACCEPTED_DECISIONS).map(([key, decision]) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        conclusionForm.decision_type === key 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-slate-200 hover:border-green-300 hover:bg-green-50/50'
                      }`}
                      onClick={() => setConclusionForm(prev => ({ ...prev, decision_type: key, rejection_reason: '', rejection_photos: [] }))}
                      data-testid={`decision-${key}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{decision.icon}</span>
                        <div className="flex-1">
                          <div className="font-semibold">{decision.label}</div>
                          <div className="text-sm text-slate-600">{decision.description}</div>
                        </div>
                        {conclusionForm.decision_type === key && (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              {/* Décisions Rejetées */}
              <TabsContent value="rejected" className="space-y-4 mt-4">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
                  <strong>⚠️ Rappel:</strong> Un dossier rejeté (sauf geste commercial) nécessite obligatoirement:
                  <ul className="list-disc list-inside mt-1">
                    <li>Un motif de rejet précis</li>
                    <li>Au moins une photo de preuve</li>
                  </ul>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(REJECTED_DECISIONS).map(([key, decision]) => (
                    <div 
                      key={key}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        conclusionForm.decision_type === key 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/50'
                      }`}
                      onClick={() => setConclusionForm(prev => ({ ...prev, decision_type: key }))}
                      data-testid={`decision-${key}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{decision.icon}</span>
                        <div className="flex-1">
                          <div className="font-semibold">{decision.label}</div>
                          <div className="text-sm text-slate-600">{decision.description}</div>
                          {decision.isCommercialGesture && (
                            <Badge className="mt-1 bg-amber-100 text-amber-700">Geste commercial</Badge>
                          )}
                        </div>
                        {conclusionForm.decision_type === key && (
                          <CheckCircle className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
            
            {/* Champs conditionnels selon la décision */}
            {conclusionForm.decision_type && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-semibold">Détails de la décision</h4>
                
                {/* Montant (si requis) */}
                {(ACCEPTED_DECISIONS[conclusionForm.decision_type]?.requiresAmount || 
                  REJECTED_DECISIONS[conclusionForm.decision_type]?.requiresAmount) && (
                  <div>
                    <Label>Montant (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={conclusionForm.amount || ''}
                      onChange={(e) => setConclusionForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || null }))}
                      placeholder="0.00"
                      className="mt-1"
                    />
                  </div>
                )}
                
                {/* Pourcentage (si remboursement partiel ou geste commercial) */}
                {(ACCEPTED_DECISIONS[conclusionForm.decision_type]?.requiresPercentage || 
                  REJECTED_DECISIONS[conclusionForm.decision_type]?.requiresPercentage) && (
                  <div>
                    <Label>Pourcentage (%)</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={conclusionForm.percentage || ''}
                      onChange={(e) => setConclusionForm(prev => ({ ...prev, percentage: parseFloat(e.target.value) || null }))}
                      placeholder="50"
                      className="mt-1"
                    />
                  </div>
                )}
                
                {/* Supplément (si remplacement avec écart) */}
                {ACCEPTED_DECISIONS[conclusionForm.decision_type]?.requiresSupplement && (
                  <div>
                    <Label>Supplément à régler par le client (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={conclusionForm.supplement_amount || ''}
                      onChange={(e) => setConclusionForm(prev => ({ ...prev, supplement_amount: parseFloat(e.target.value) || null }))}
                      placeholder="0.00"
                      className="mt-1"
                    />
                  </div>
                )}
                
                {/* Nouveau produit (si remplacement) */}
                {ACCEPTED_DECISIONS[conclusionForm.decision_type]?.newWarranty && (
                  <div>
                    <Label>Description du nouveau produit</Label>
                    <Input
                      value={conclusionForm.new_product_description}
                      onChange={(e) => setConclusionForm(prev => ({ ...prev, new_product_description: e.target.value }))}
                      placeholder="Ex: iPhone 13 Pro Max 256Go"
                      className="mt-1"
                    />
                    <p className="text-xs text-green-600 mt-1">✓ Une nouvelle garantie sera calculée automatiquement</p>
                  </div>
                )}
                
                {/* Référence devis (si réparation payante) */}
                {REJECTED_DECISIONS[conclusionForm.decision_type]?.requiresQuote && (
                  <div>
                    <Label>Référence du devis</Label>
                    <Input
                      value={conclusionForm.quote_reference}
                      onChange={(e) => setConclusionForm(prev => ({ ...prev, quote_reference: e.target.value }))}
                      placeholder="DEVIS-2024-XXXX"
                      className="mt-1"
                    />
                  </div>
                )}
                
                {/* Motif de rejet (obligatoire pour les rejets non commerciaux) */}
                {REJECTED_DECISIONS[conclusionForm.decision_type] && 
                 !REJECTED_DECISIONS[conclusionForm.decision_type]?.isCommercialGesture && (
                  <div className="space-y-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <Label className="text-red-700">Motif de rejet *</Label>
                      <Select 
                        value={conclusionForm.rejection_reason} 
                        onValueChange={(v) => setConclusionForm(prev => ({ ...prev, rejection_reason: v }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionnez un motif" />
                        </SelectTrigger>
                        <SelectContent>
                          {config?.rejection_reasons && Object.entries(config.rejection_reasons).map(([key, reason]) => (
                            <SelectItem key={key} value={key}>
                              {reason.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {conclusionForm.rejection_reason && config?.rejection_reasons?.[conclusionForm.rejection_reason] && (
                        <p className="text-xs text-red-600 mt-1">
                          {config.rejection_reasons[conclusionForm.rejection_reason].description}
                        </p>
                      )}
                    </div>
                    
                    {/* Photos de preuve (obligatoires) */}
                    <div>
                      <Label className="text-red-700 flex items-center gap-2">
                        <Camera className="h-4 w-4" /> Photos de preuve *
                      </Label>
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleConclusionPhotoUpload}
                          className="hidden"
                          id="conclusion-photo-upload"
                        />
                        <label
                          htmlFor="conclusion-photo-upload"
                          className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-red-300 rounded-lg cursor-pointer hover:bg-red-100"
                        >
                          <Upload className="h-4 w-4" />
                          Ajouter une photo
                        </label>
                      </div>
                      {conclusionForm.rejection_photos.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {conclusionForm.rejection_photos.map((photo, i) => (
                            <div key={i} className="relative">
                              <img src={photo} alt={`Photo ${i+1}`} className="h-20 w-20 object-cover rounded border" />
                              <button
                                type="button"
                                onClick={() => setConclusionForm(prev => ({
                                  ...prev,
                                  rejection_photos: prev.rejection_photos.filter((_, idx) => idx !== i)
                                }))}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {conclusionForm.rejection_photos.length === 0 && (
                        <p className="text-xs text-red-600 mt-1">⚠️ Au moins une photo de preuve est obligatoire</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Description personnalisée */}
                <div>
                  <Label>Description personnalisée (optionnel)</Label>
                  <Textarea
                    value={conclusionForm.custom_description}
                    onChange={(e) => setConclusionForm(prev => ({ ...prev, custom_description: e.target.value }))}
                    placeholder="Laisser vide pour utiliser la description automatique..."
                    rows={2}
                    className="mt-1"
                  />
                </div>
                
                {/* Notes technicien */}
                <div>
                  <Label>Notes du technicien</Label>
                  <Textarea
                    value={conclusionForm.technician_notes}
                    onChange={(e) => setConclusionForm(prev => ({ ...prev, technician_notes: e.target.value }))}
                    placeholder="Notes internes sur la décision..."
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowConclusionModal(false); setConclusionForm(initialConclusionState); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleConcludeDossier}
              disabled={!conclusionForm.decision_type}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Valider la conclusion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
